import { Button } from "@/components/ui/button";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/autoplay';
import { HeroSlide } from "../../types";

interface HeroProps {
  slides: HeroSlide[];
}

const Hero = ({ slides }: HeroProps) => {

  return (
    <section className="w-full relative overflow-hidden">
      <style>{`
        .swiper-button-next, .swiper-button-prev {
          background: transparent !important;
          color: #6b7280 !important;
          border: none !important;
          box-shadow: none !important;
        }
        .swiper-button-next:hover, .swiper-button-prev:hover {
          background: rgba(255, 255, 255, 0.1) !important;
        }
      `}</style>
      <Swiper
        modules={[Navigation, Autoplay]}
        spaceBetween={0}
        slidesPerView={1}
        navigation
        autoplay={{ delay: 5000, disableOnInteraction: false }}
        loop
        className="w-full"
      >
        {slides.length > 0 ? (
          slides.map((slide) => (
            <SwiperSlide key={slide.id}>
              <div className="relative h-[70vh] md:h-[80vh] w-full overflow-hidden">
                {/* Background Image */}
                <div className="absolute inset-0">
                  <img
                    src={slide.image_url}
                    alt={slide.title}
                    className="w-full h-full object-cover scale-105 transition-transform duration-[10s] ease-out hover:scale-110"
                  />
                  {/* Sophisticated overlay system */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-transparent" />
                  <div className="absolute inset-0 bg-neutral-900/20" />
                </div>

                {/* Subtle background elements - minimal brand accent */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-brand-primary/5 rounded-full blur-3xl animate-pulse" />
                  <div className="absolute bottom-1/4 left-1/3 w-80 h-80 bg-brand-secondary/5 rounded-full blur-3xl animate-pulse delay-2000" />
                </div>

                {/* Content Container */}
                <div className="absolute inset-0 flex items-center justify-start">
                  <div className="container mx-auto px-6 md:px-12 lg:px-16">
                    <div className="max-w-4xl space-y-8 animate-fadeInUp">
                      {/* Title with sophisticated typography */}
                      <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-tight">
                        <span className="block text-white drop-shadow-2xl mb-2">
                          {slide.title.split(" ").slice(0, -1).join(" ")}
                        </span>
                        {/* Strategic use of brand gradient for key words only */}
                        <span className="block text-brand-gradient drop-shadow-2xl font-extrabold">
                          {slide.title.split(" ").slice(-1)}
                        </span>
                      </h1>

                      {/* Subtitle with refined spacing */}
                      {slide.subtitle && (
                        <div className="relative max-w-2xl">
                          <p className="text-lg md:text-xl lg:text-2xl text-white/90 leading-relaxed drop-shadow-lg">
                            {slide.subtitle}
                          </p>
                          {/* Subtle decorative line */}
                          <div className="mt-6 w-20 h-0.5 bg-gradient-to-r from-white/60 to-transparent rounded-full" />
                        </div>
                      )}

                      {/* Call to Action - Primary use of brand colors */}
                      {slide.button_text && (
                        <div className="pt-6">
                          <Button
                            variant="brand"
                            size="lg"
                            asChild
                            className="text-lg px-8 py-6 rounded-xl group"
                          >
                            <a
                              href={slide.button_link}
                              className="flex items-center gap-3"
                            >
                              {slide.button_text}
                              <svg
                                className="w-5 h-5 transition-transform group-hover:translate-x-1"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                                />
                              </svg>
                            </a>
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))
        ) : (
          <SwiperSlide>
            <div className="relative h-[70vh] md:h-[80vh] w-full overflow-hidden">
              {/* Background with brand gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-brand-primary/20 via-brand-secondary/10 to-neutral-100 dark:from-brand-primary/10 dark:via-brand-secondary/5 dark:to-neutral-900" />
              {/* Subtle background elements */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-brand-primary/5 rounded-full blur-3xl animate-pulse" />
                <div className="absolute bottom-1/4 left-1/3 w-80 h-80 bg-brand-secondary/5 rounded-full blur-3xl animate-pulse delay-2000" />
              </div>

              {/* Content */}
              <div className="absolute inset-0 flex items-center justify-start">
                <div className="container mx-auto px-6 md:px-12 lg:px-16">
                  <div className="max-w-4xl space-y-8 animate-fadeInUp">
                    <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-tight">
                      <span className="block text-neutral-900 dark:text-neutral-100 drop-shadow-2xl mb-2">
                        ¡Bienvenido a la
                      </span>
                      <span className="block text-brand-gradient drop-shadow-2xl font-extrabold">
                        Tienda!
                      </span>
                    </h1>

                    <div className="relative max-w-2xl">
                      <p className="text-lg md:text-xl lg:text-2xl text-neutral-700 dark:text-neutral-300 leading-relaxed drop-shadow-lg">
                        Descubre productos increíbles con la mejor calidad y precios excepcionales
                      </p>
                      <div className="mt-6 w-20 h-0.5 bg-gradient-to-r from-brand-primary to-brand-secondary rounded-full" />
                    </div>

                    <div className="pt-6">
                      <Button
                        variant="brand"
                        size="lg"
                        asChild
                        className="text-lg px-8 py-6 rounded-xl group"
                      >
                        <a
                          href="/products"
                          className="flex items-center gap-3"
                        >
                          Explorar Productos
                          <svg
                            className="w-5 h-5 transition-transform group-hover:translate-x-1"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17 8l4 4m0 0l-4 4m4-4H3"
                            />
                          </svg>
                        </a>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </SwiperSlide>
        )}
      </Swiper>
    </section>
  );
};


export default Hero;